import React from 'react';
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import { Timer } from '@mui/icons-material';
import Stopwatch from '../../Stopwatch';
import { type FriendlyState, getFriendlyLabel } from './friendlyToolConfig';

interface FriendlyPartDisplayProps {
    toolName: string;
    state: FriendlyState;
    args?: any;
    isError?: boolean;
    startTime?: Date;
    endTime?: Date;
    streamedBytes?: number;
}

const BYTES_PER_BUCKET = 500;
const BUCKET_HEIGHT = 14;
const BUCKET_WIDTH = 8;

const COMPACT_THRESHOLDS: [number, string][] = [
    [1_000_000_000, 'Gb'],
    [1_000_000, 'Mb'],
    [1_000, 'Kb'],
];

function ByteBuckets({ streamedBytes = 0 }: { streamedBytes: number }) {
    const fullBuckets = Math.floor(streamedBytes / BYTES_PER_BUCKET);
    const partialFill = (streamedBytes % BYTES_PER_BUCKET) / BYTES_PER_BUCKET;
    const totalBuckets = fullBuckets + (partialFill > 0 ? 1 : 0);
    const visibleBuckets = Math.max(totalBuckets, 1);
    const theme = useTheme();

    function formatCompact(value: number, decimals = 2, addSuffix: boolean = true): string {
        const abs = Math.abs(value);
        for (const [threshold, suffix] of COMPACT_THRESHOLDS) {
            if (abs >= threshold) {
                return (value / threshold).toFixed(decimals) + (addSuffix ? suffix : "");
            }
        }
        return value.toFixed(0)+"b";
    }

    const formatBytes = (bytes: number) => {
        return formatCompact(bytes);
    }

    return (
        <>
            <Box sx={{
                display: 'inline-flex',
                alignItems: 'flex-end',
                alignContent: 'center',
                gap: '3px',
                ml: 1,
                height: `${BUCKET_HEIGHT + 2}px`,
            }}>
                {Array.from({ length: visibleBuckets }, (_, i) => {
                    const isFull = i < fullBuckets;
                    const isPartial = i === fullBuckets && partialFill > 0;
                    const fillRatio = isFull ? 1 : isPartial ? partialFill : 0;
                    const isActive = isPartial && !isFull;

                    return (
                        <Box
                            key={i}
                            sx={{
                                position: 'relative',
                                width: `${BUCKET_WIDTH}px`,
                                height: `${BUCKET_HEIGHT}px`,
                                borderRadius: '2px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                overflow: 'hidden',
                                flexShrink: 0,
                            }}
                        >
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: `${fillRatio * 100}%`,
                                backgroundColor: isFull
                                    ? alpha(theme.palette.primary.main,0.6)
                                    : alpha(theme.palette.primary.main,0.4),
                                transition: 'height 0.15s ease-out',
                                ...(isActive ? {
                                    animation: 'bucketPulse 0.8s ease-in-out infinite',
                                    '@keyframes bucketPulse': {
                                        '0%, 100%': { opacity: 0.4 },
                                        '50%': { opacity: 0.9 },
                                    },
                                } : {}),
                            }} />
                        </Box>
                    );
                })}
            </Box>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ fontSize: '0.78rem' }}>
                <Typography variant='body1' sx={{ display: 'inline-flex' }}>{formatBytes(streamedBytes)}</Typography>
            </Stack>
        </>
    );
}

const FriendlyPartDisplay: React.FC<FriendlyPartDisplayProps> = ({
    toolName,
    state,
    args,
    isError,
    startTime,
    endTime,
    streamedBytes,
}) => {
    const label = getFriendlyLabel(toolName, state, args, isError);

    const elapsedMs = startTime && endTime
        ? endTime.getTime() - startTime.getTime()
        : undefined;

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
                py: 0.5,
                px: 0.5,
                color: isError ? 'warning.main' : 'text.secondary',
                fontSize: '0.82rem',
                minHeight: '28px',
                alignItems: 'center',
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: 'inherit',
                    fontStyle: 'italic',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {label}
            </Typography>

            {state === 'partial' && <ByteBuckets streamedBytes={streamedBytes ?? 0} />}

            {(state === 'call') && startTime && (
                <>
                    <ByteBuckets streamedBytes={streamedBytes ?? 0} />
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ fontSize: '0.78rem' }}>
                        <Timer sx={{ fontSize: '0.9rem' }} />
                        <Stopwatch startingPoint={startTime} on={true} style={{ fontSize: '0.78rem' }} />
                    </Stack>
                </>
            )}

            {state === 'response' && elapsedMs != null && elapsedMs > 0 && (
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>
                    <Timer sx={{ fontSize: '0.9rem' }} />
                    <Typography component="span" sx={{ fontSize: 'inherit' }}>
                        {(elapsedMs / 1000).toFixed(2)}s
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default FriendlyPartDisplay;
